import { REALTIME_WINDOW_MINUTES_OPTIONS } from "../utils/realtime";

import settingsIcon from "../../../assets/imgs/brm/setting-icon.svg";
import downloadIcon from "../../../assets/imgs/brm/download-icon.svg";
import mapIcon from "../../../assets/imgs/brm/map-icon.svg";

export type VehicleOption = { value: number; label: string };

// 車両選択・realtime/period切替・期間入力・設定/DL/地図導線をまとめたヘッダー。
export const BpsLogHeader = (props: {
  logoSrc: string;

  vehicleId: number | null;
  vehicleOptions: VehicleOption[];
  onVehicleChange: (vehicleId: number) => void;

  isRealtime: boolean;
  setMonitorMode: (nextIsRealtime: boolean) => void;

  realtimeWindowMinutes: number;
  onRealtimeWindowMinutesChange: (minutes: number) => void;

  startInput: string;
  endInput: string;
  onStartInputChange: (value: string) => void;
  onEndInputChange: (value: string) => void;

  quickEndOffsetMinutes: string;
  onQuickEndOffsetMinutesChange: (minutes: number) => void;
  quickEndOffsetMinutesOptions: readonly number[];

  canApplyPeriodRange: boolean;
  onApplyPeriodRange: () => void;
  onCancelPeriodRange: () => void;
  isBpslogRangeLoading: boolean;
  isPeriodRenderBusy: boolean;

  forceLatest: boolean;
  onForceLatestChange: (next: boolean) => void;

  chartsMeta: {
    selectedText: string;
    widthText: string;
  };

  onOpenDownload: () => void;
  onOpenSettings: () => void;
  onOpenMap: () => void;
}) => {
  const {
    logoSrc,
    vehicleId,
    vehicleOptions,
    onVehicleChange,
    isRealtime,
    setMonitorMode,
    realtimeWindowMinutes,
    onRealtimeWindowMinutesChange,
    startInput,
    endInput,
    onStartInputChange,
    onEndInputChange,
    quickEndOffsetMinutes,
    onQuickEndOffsetMinutesChange,
    quickEndOffsetMinutesOptions,
    canApplyPeriodRange,
    onApplyPeriodRange,
    onCancelPeriodRange,
    isBpslogRangeLoading,
    isPeriodRenderBusy,
    forceLatest,
    onForceLatestChange,
    chartsMeta,
    onOpenDownload,
    onOpenSettings,
    onOpenMap,
  } = props;

  const isApplyingPeriodRange = isBpslogRangeLoading || isPeriodRenderBusy;

  return (
    <header className="bpslogHeader">
      <div className="bpslogHeaderLeft">
        <div className="bpslogLogo">
          <img src={logoSrc} alt="" />
        </div>
        <label className="bpslogField bpslogField--vehicle">
          <select
            value={vehicleId ?? ""}
            onChange={(e) => onVehicleChange(Number(e.target.value))}
            disabled={!vehicleOptions.length}
          >
            {!vehicleOptions.length ? (
              <option value="" disabled>
                車両なし
              </option>
            ) : null}
            {vehicleOptions.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </label>
      </div>

      <div className="bpslogHeaderCenter">
        <div
          className="bpslogRadioGroup"
          role="radiogroup"
          aria-label="監視モード"
        >
          <label className={`bpslogRadio ${isRealtime ? "isActive" : ""}`}>
            <input
              type="radio"
              name="monitorMode"
              checked={isRealtime}
              onChange={() => setMonitorMode(true)}
            />
            リアルタイム
          </label>
          <label className={`bpslogRadio ${!isRealtime ? "isActive" : ""}`}>
            <input
              type="radio"
              name="monitorMode"
              checked={!isRealtime}
              onChange={() => setMonitorMode(false)}
            />
            期間指定
          </label>
        </div>

        <div className="bpslogTimeGroup" aria-label="期間">
          <label className="bpslogField bpslogField--start">
            {isRealtime ? null : <span>開始</span>}
            {isRealtime ? (
              <select
                className="bpslogRealtimeWindowSelect"
                value={realtimeWindowMinutes}
                onChange={(e) =>
                  onRealtimeWindowMinutesChange(Number(e.target.value))
                }
                aria-label="リアルタイム表示"
              >
                {REALTIME_WINDOW_MINUTES_OPTIONS.map((m) => (
                  <option key={m} value={m}>
                    {m} 分間
                  </option>
                ))}
              </select>
            ) : (
              <input
                type="datetime-local"
                value={startInput}
                onChange={(e) => onStartInputChange(e.target.value)}
                step={1}
              />
            )}
          </label>

          {isRealtime ? null : (
            <>
              <label className="bpslogField bpslogField--end">
                <span>終了</span>
                <input
                  type="datetime-local"
                  value={endInput}
                  onChange={(e) => onEndInputChange(e.target.value)}
                  step={1}
                />
                <select
                  className="bpslogQuickEndSelect"
                  aria-label="終了時刻クイック設定"
                  value={quickEndOffsetMinutes}
                  onChange={(e) => {
                    const m = Number(e.target.value);
                    if (!Number.isFinite(m) || m < 0) return;
                    onQuickEndOffsetMinutesChange(m);
                  }}
                >
                  <option value="" disabled>
                    開始＋
                  </option>
                  {quickEndOffsetMinutesOptions.map((m) => (
                    <option key={m} value={m}>
                      +{m}分
                    </option>
                  ))}
                </select>
              </label>
              <button
                type="button"
                className="bpslogApplyButton"
                onClick={onApplyPeriodRange}
                disabled={!canApplyPeriodRange || isApplyingPeriodRange}
              >
                {isApplyingPeriodRange ? "取得中" : "適用"}
              </button>

              <button
                type="button"
                className="bpslogCancelButton"
                onClick={onCancelPeriodRange}
                disabled={!isApplyingPeriodRange}
              >
                キャンセル
              </button>
            </>
          )}
          <div className="bpslogApplyMeta" aria-label="最新選択">
            <label className="bpslogForceLatestLabel bpslogApplyMetaForceLatest">
              <input
                type="checkbox"
                checked={forceLatest}
                onChange={(e) => onForceLatestChange(e.target.checked)}
              />
              <span>最新選択</span>
            </label>
            <div
              className="bpslogApplyMetaCharts"
              aria-label="選択時刻と表示幅"
            >
              <div className="bpslogApplyMetaChartItem">
                <span className="bpslogChartsMetaLabel">選択時刻</span>
                <span
                  className="bpslogChartsMetaSelected"
                  title={chartsMeta.selectedText}
                >
                  {chartsMeta.selectedText}
                </span>
              </div>
              <div className="bpslogApplyMetaChartItem">
                <span className="bpslogChartsMetaLabel">表示幅</span>
                <span
                  className="bpslogChartsMetaValue"
                  title={chartsMeta.widthText}
                >
                  {chartsMeta.widthText}
                </span>
              </div>
            </div>
          </div>
        </div>

        <div className="bpslogHeaderControls" />
      </div>

      <div className="bpslogHeaderRight" aria-label="ヘッダー右側">
        <button
          type="button"
          className="bpslogDownloadButton"
          aria-label="LOG DOWNLOAD"
          onClick={onOpenDownload}
        >
          <img
            src={downloadIcon}
            className="bpslogDownloadIcon"
            alt=""
            aria-hidden="true"
          />
          <span className="bpslogSrOnly">LOG DOWNLOAD</span>
        </button>
        <button
          type="button"
          className="bpslogSettingsButton"
          aria-label="設定"
          onClick={onOpenSettings}
        >
          <img
            src={settingsIcon}
            className="bpslogSettingsIcon"
            alt=""
            aria-hidden="true"
          />
          <span className="bpslogSrOnly">設定</span>
        </button>
        <button
          type="button"
          className="bpslogMapButton"
          aria-label="地図"
          onClick={onOpenMap}
        >
          <img
            src={mapIcon}
            className="bpslogMapIcon"
            alt=""
            aria-hidden="true"
          />
          <span className="bpslogSrOnly">地図</span>
        </button>
      </div>
    </header>
  );
};
